import {
  type CommunityDepositInfo,
  type CommunityDepositResult,
  type CommunityRedeemResponse,
  type DepositDeleteResult,
  type TtlKind,
  communityDepositInfoSchema,
  communityDepositResultSchema,
  communityRedeemResponseSchema,
  depositDeleteResultSchema,
} from '@xfile/shared';
import { nowMs, requestIdOf, traceApi } from '../diagnostics/trace';

/**
 * Client du LIEN D'ACCÈS COMMUNAUTAIRE côté PUBLIC (XFILE-08 ; ADR-0005 partie b).
 *
 * `redeemCommunity` échange un jeton contre une VRAIE session forcée en rôle `user`
 * (lecture seule) — même forme de réponse que le login (`{ slot, user }`). L'appel est
 * NON authentifié (page publique `/join/:token`, aucun slot/cookie préalable) mais pose
 * le cookie de session (faisceau chiffré) côté serveur : d'où `credentials: 'same-origin'`.
 * La GESTION de l'accès communautaire (statut/rotation/révocation, superadmin) vit dans
 * `admin-api.ts` — ce module ne porte que l'échange public.
 */

/** Erreur d'échange de jeton : statut HTTP + code métier normalisé. */
export class CommunityApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'CommunityApiError';
  }
}

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON : code générique.
  }
  return 'UNKNOWN';
}

/**
 * `POST /api/community/redeem` (PUBLIC) — échange `token` contre `{ slot, user }`.
 * Lève `CommunityApiError` : `403` accès communautaire désactivé, `401` jeton
 * invalide/révoqué, `400` jeton mal formé, autres = panne réseau/serveur.
 */
export async function redeemCommunity(token: string): Promise<CommunityRedeemResponse> {
  const path = '/api/community/redeem';
  const started = nowMs();
  let res: Response;
  try {
    res = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch (err) {
    traceApi('POST', path, 0, Math.round(nowMs() - started), null);
    throw err;
  }
  traceApi('POST', path, res.status, Math.round(nowMs() - started), requestIdOf(res));
  if (!res.ok) throw new CommunityApiError(res.status, await errorCodeOf(res));
  return communityRedeemResponseSchema.parse(await res.json());
}

/**
 * `GET /api/community/deposit/:token` (PUBLIC) — de quoi afficher la page de dépôt
 * (interrupteur global, nom de l'app, validité du jeton). `fetch` NU (page publique
 * déconnectée). Lève `CommunityApiError` sur réponse non-OK.
 */
export async function depositInfo(token: string): Promise<CommunityDepositInfo> {
  const path = `/api/community/deposit/${token}`;
  const started = nowMs();
  let res: Response;
  try {
    res = await fetch(path, { credentials: 'same-origin' });
  } catch (err) {
    traceApi('GET', path, 0, Math.round(nowMs() - started), null);
    throw err;
  }
  traceApi('GET', path, res.status, Math.round(nowMs() - started), requestIdOf(res));
  if (!res.ok) throw new CommunityApiError(res.status, await errorCodeOf(res));
  return communityDepositInfoSchema.parse(await res.json());
}

/**
 * `POST /api/community/deposit/:token/upload` (PUBLIC, multipart) — dépose des fichiers
 * avec la durée de conservation choisie (`ttlKind`) et récupère le LIEN DE PARTAGE public
 * (`share`) + les fichiers ignorés (`skipped`). Le champ `ttlKind` PRÉCÈDE les parties
 * fichier (le serveur le lit avant le 1er fichier). NE PAS poser de `Content-Type` : le
 * navigateur pose le boundary multipart. Lève `CommunityApiError` sur réponse non-OK.
 *
 * La réponse porte aussi, depuis XFILE-36/-37, le lien du DOSSIER DE LOT (`folder`, présent
 * dès UN fichier) et la CAPACITÉ D'EFFACEMENT du déposant (`manage`). Les deux sont ADDITIFS
 * dans `communityDepositResultSchema` (optionnels) : le `parse` ci-dessous les laisse passer
 * sans changement ici, et un serveur antérieur qui ne les envoie pas reste accepté.
 */
export async function depositUpload(
  token: string,
  files: File[],
  ttlKind: TtlKind,
): Promise<CommunityDepositResult> {
  const path = `/api/community/deposit/${token}/upload`;
  const form = new FormData();
  // Le champ TTL doit précéder les fichiers dans le flux multipart (lu avant le 1er fichier).
  form.append('ttlKind', ttlKind);
  for (const file of files) form.append('file', file);
  const started = nowMs();
  let res: Response;
  try {
    res = await fetch(path, { method: 'POST', credentials: 'same-origin', body: form });
  } catch (err) {
    traceApi('POST', path, 0, Math.round(nowMs() - started), null);
    throw err;
  }
  traceApi('POST', path, res.status, Math.round(nowMs() - started), requestIdOf(res));
  if (!res.ok) throw new CommunityApiError(res.status, await errorCodeOf(res));
  return communityDepositResultSchema.parse(await res.json());
}

/**
 * `POST /api/community/deposit/manage/:token/delete` (PUBLIC, XFILE-37) — le DÉPOSANT ANONYME
 * retire des fichiers de SON dépôt. `manageToken` est la capacité rendue par le dépôt (jeton
 * opaque, fenêtre d'une heure), l'UNIQUE autorisation : ni session ni compte derrière ce
 * visiteur, d'où le `fetch` NU (comme `depositUpload`, jamais `apiFetch` qui porterait un slot
 * de session inexistant).
 *
 * Statuts à distinguer côté appelant via `CommunityApiError` :
 *  - **401** jeton inconnu (jamais émis, ou perdu au redémarrage du serveur) ;
 *  - **410** fenêtre ÉCOULÉE — le cas qui mérite un message propre : ce n'est pas un échec
 *    technique, c'est un délai passé, et l'affordance doit disparaître ;
 *  - **403** un id hors du dépôt (le serveur refuse le lot ENTIER, jamais partiellement) ;
 *  - **400** corps invalide (liste vide ou au-delà de `BULK_FILES_MAX`).
 *
 * ⚠ La trace de diagnostic porte le chemin REDACTÉ (`…/manage/:token/delete`), jamais le jeton :
 * les anneaux de `diagnostics/` alimentent « Signaler un bug », dont le contenu est destiné à
 * être copié et transmis. Le jeton de dépôt de `depositInfo`/`depositUpload` est, lui, un lien
 * PUBLIC fait pour circuler ; celui-ci est une capacité de DESTRUCTION — il n'a rien à faire
 * dans un rapport de bug.
 */
export async function deleteDepositFiles(
  manageToken: string,
  fileIds: string[],
): Promise<DepositDeleteResult> {
  const path = `/api/community/deposit/manage/${encodeURIComponent(manageToken)}/delete`;
  const tracedPath = '/api/community/deposit/manage/:token/delete';
  const started = nowMs();
  let res: Response;
  try {
    res = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileIds }),
    });
  } catch (err) {
    traceApi('POST', tracedPath, 0, Math.round(nowMs() - started), null);
    throw err;
  }
  traceApi('POST', tracedPath, res.status, Math.round(nowMs() - started), requestIdOf(res));
  if (!res.ok) throw new CommunityApiError(res.status, await errorCodeOf(res));
  return depositDeleteResultSchema.parse(await res.json());
}
